// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { createAppLogger } from '@/services/appLogger';
import { buildAccessHeaders } from '@/services/claimHttp';
import { getSharedBffWebSocket, type BffInboundMessage } from '@/services/bffWebSocket';
import { PHOTO_UPLOAD_TIMEOUT_MS } from '@/features/tour-photos/constants';

import type { StakeUploadEntry, StakeUploadJob } from './types';

/**
 * Module-scoped upload queue for boundary stakes.
 *
 * Same shape as the tour-photo queue and for the same reason: the mapper walks on to the next
 * stake the moment the shutter closes, and an upload held in component state would die with
 * the screen. Three legs — presign over the WebSocket, PUT the bytes to S3, POST the stake —
 * and a stake without a photo skips the first two. The metadata leg is what makes the stake
 * exist; a photo that fails is retried with it, and a stake never waits on its picture.
 */

type Listener = (entries: StakeUploadEntry[]) => void;

let entries: StakeUploadEntry[] = [];
let draining = false;
const listeners = new Set<Listener>();

const emit = () => {
  const snapshot = [...entries];
  listeners.forEach((listener) => listener(snapshot));
};

export const subscribeToStakeUploads = (listener: Listener): (() => void) => {
  listeners.add(listener);
  listener([...entries]);
  return () => {
    listeners.delete(listener);
  };
};

const setEntry = (id: string, patch: Partial<StakeUploadEntry>) => {
  entries = entries.map((entry) => (entry.job.stake.id === id ? { ...entry, ...patch } : entry));
  emit();
};

/** Queues a stake. Returns immediately; progress is observable via {@link subscribeToStakeUploads}. */
export const enqueueStakeUpload = (job: StakeUploadJob): void => {
  entries = [...entries, { job, status: 'pending', error: null }];
  emit();
  void drain();
};

/** Puts a failed stake back in line. The same id, so the server sees a retry, not a twin. */
export const retryStakeUpload = (id: string): void => {
  setEntry(id, { status: 'pending', error: null });
  void drain();
};

const drain = async (): Promise<void> => {
  if (draining) {
    return;
  }
  draining = true;

  try {
    let next = entries.find((entry) => entry.status === 'pending');
    while (next) {
      const id = next.job.stake.id;
      setEntry(id, { status: 'uploading' });
      try {
        await uploadOne(next.job);
        setEntry(id, { status: 'done' });
      } catch (cause) {
        setEntry(id, { status: 'failed', error: cause instanceof Error ? cause.message : 'upload failed' });
      }
      next = entries.find((entry) => entry.status === 'pending');
    }
  } finally {
    draining = false;
  }
};

type PresignAck = { uploadUrl: string; key: string };

const uploadOne = async (job: StakeUploadJob): Promise<void> => {
  const apiBaseUrl = getApiBaseUrl();
  const token = getAuthToken();
  if (!token) {
    throw new Error('stake upload requires a signed-in admin');
  }

  const { stake, photo } = job;
  const log = createAppLogger(apiBaseUrl, token, { source: 'course-stake-upload' });
  log('stake upload start', {
    stakeId: stake.id,
    courseId: stake.courseId,
    holeNumber: stake.holeNumber,
    hasPhoto: photo !== null,
  });

  let photoKey: string | null = null;
  if (photo) {
    const { uploadUrl, key } = await requestPresign(apiBaseUrl, job, token);
    const putResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': photo.contentType,
        // Byte-identical to what the BFF signed; see S3TourImageStorage.
        'Cache-Control': 'public, max-age=31536000, immutable',
      },
      body: photo.blob,
    });
    if (!putResponse.ok) {
      const body = await putResponse.text().catch(() => '');
      log('stake photo PUT rejected', { stakeId: stake.id, status: putResponse.status }, 'error');
      throw new Error(`photo upload failed with ${putResponse.status} ${body.slice(0, 200)}`);
    }
    photoKey = key;
  }

  const response = await fetch(
    `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(stake.courseId)}/stakes`,
    {
      method: 'POST',
      headers: { ...buildAccessHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: stake.id,
        holeNumber: stake.holeNumber,
        stakeKind: stake.stakeKind,
        lat: stake.lat,
        lon: stake.lon,
        accuracyMeters: stake.accuracyMeters,
        headingDegrees: stake.headingDegrees,
        headingAccuracy: stake.headingAccuracy,
        capturedAt: stake.capturedAt,
        photoKey,
      }),
    },
  );

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    log('stake upload rejected', { stakeId: stake.id, status: response.status, body: body.slice(0, 400) }, 'error');
    throw new Error(`saving the stake failed with ${response.status} ${body.slice(0, 200)}`);
  }

  log('stake upload done', { stakeId: stake.id, photoKey });
};

const requestPresign = (apiBaseUrl: string, job: StakeUploadJob, token: string): Promise<PresignAck> => {
  const socket = getSharedBffWebSocket(apiBaseUrl);
  socket.setAuthToken(token);
  const { stake, photo } = job;

  return new Promise<PresignAck>((resolve, reject) => {
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeout);
      unsubscribe();
      fn();
    };

    const timeout = setTimeout(
      () => finish(() => reject(new Error('course.stake.presign timed out'))),
      PHOTO_UPLOAD_TIMEOUT_MS,
    );

    const unsubscribe = socket.onMessage((inbound: BffInboundMessage) => {
      const message = inbound as {
        type?: string;
        stakeId?: string;
        uploadUrl?: string;
        key?: string;
        message?: string;
      };
      if (message.type !== 'course.stake.presign.ack' && message.type !== 'course.stake.presign.error') {
        return;
      }
      if (message.stakeId !== stake.id) {
        return;
      }
      if (message.type === 'course.stake.presign.ack') {
        const { uploadUrl, key } = message;
        if (!uploadUrl || !key) {
          finish(() => reject(new Error('course.stake.presign.ack was missing uploadUrl or key')));
          return;
        }
        finish(() => resolve({ uploadUrl, key }));
        return;
      }
      finish(() => reject(new Error(message.message ?? 'presign rejected')));
    });

    socket.send({
      type: 'course.stake.presign',
      courseId: stake.courseId,
      stakeId: stake.id,
      requestId: stake.id,
      fileName: photo?.fileName,
      contentType: photo?.contentType,
      fileSize: photo?.byteSize,
    });
  });
};

let authToken: string | null = null;

export const setStakeUploadAuthToken = (token: string | null | undefined): void => {
  authToken = token?.trim() || null;
};

const getAuthToken = (): string | null => authToken;

/** Test seam. */
export const __resetStakeUploadQueue = (): void => {
  entries = [];
  draining = false;
  listeners.clear();
  authToken = null;
};
